import { useEffect, useMemo, useState } from 'react'
import { chipClass } from '@/components/analytics/AnalyticsSection'
import { MarketMoneyPeriodPicker } from '@/components/analytics/AnalyticsPeriodPicker'
import { useAnalyticsPeriod } from '@/components/analytics/market-money-data'
import { UnitBreakdown } from '@/components/analytics/UnitBreakdown'
import { MarginChart, STEP_TITLES, periodBars } from '@/components/analytics/UnitCharts'
import { CopyValue, UnitPiecePreview } from '@/components/analytics/UnitPiecePreview'
import { UnitSummaryCards } from '@/components/analytics/UnitSummary'
import { WarehouseThumbnail } from '@/components/warehouse/WarehouseThumbnail'
import {
  DEFAULT_UNIT_FILTERS,
  categoryLabel,
  chartPoints,
  fetchUnitEconomics,
  formatPercent,
  formatKopecks,
  formatPerGram,
  formatRub,
  perGram,
  unitChargeAmount,
  unitOtherAmount,
  unitPenaltyAmount,
  unitServiceAmount,
  UNIT_SERVICE_CHARGE_KINDS,
  rowsInSpan,
  signClass,
  sortUnitRows,
  type UnitEconomicsReport,
  type UnitEconomicsRow,
  type UnitServerFilters,
  type UnitSortKey,
} from '@/lib/unit-economics'

const COLUMNS: ReadonlyArray<readonly [UnitSortKey, string]> = [
  ['date', 'Дата'],
  ['cost', 'Купили за'],
  ['soldFor', 'Продали за'],
  ['marketWithheld', 'Услуги ЯМ'],
  ['boost', 'Буст'],
  ['penalty', 'Штрафы'],
  ['otherCharges', 'Удержания'],
  ['received', 'Получили'],
  ['margin', 'Маржа'],
  ['marginOfCost', '%'],
  ['cdekCost', 'СДЭК'],
  ['tax', 'Налог'],
  ['profit', 'Прибыль'],
]

const GRID = 'md:grid md:grid-cols-[40px_minmax(170px,1fr)_44px_repeat(13,minmax(76px,84px))_28px] md:items-start md:gap-2'

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; report: UnitEconomicsReport }

function dateLabel(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Moscow' })
}

function spanLabel(span: { from: string; to: string }): string {
  const day = (iso: string) => `${Number(iso.slice(8, 10))}.${iso.slice(5, 7)}`
  return span.from === span.to ? day(span.from) : `${day(span.from)}–${day(span.to)}`
}

function Money({ value, weight, label, tone, testId }: { value: number | null; weight: number | null; label: string; tone?: string; testId?: string }) {
  return (
    <span className="flex flex-col md:items-end" data-label={label}>
      <span className={`tab-nums whitespace-nowrap ${tone ?? 'text-ink'}`} data-testid={testId}>{formatRub(value)}</span>
      <span className="tab-nums text-[10px] text-ink-faint">{formatPerGram(perGram(value, weight))}</span>
    </span>
  )
}

function CostValue({ row }: { row: UnitEconomicsRow }) {
  const detail = row.cost === null ? row.costReason ?? '—' : formatPerGram(perGram(row.cost, row.weightGrams))
  return (
    <span className="flex flex-col md:items-end" data-label="Купили за">
      <span className="tab-nums whitespace-nowrap text-ink" data-testid="unit-cost">{formatRub(row.cost)}</span>
      <span className="tab-nums text-[10px] text-ink-faint">{detail}</span>
    </span>
  )
}

function ChargeValue({ cash, weight, label, testId, hint }: {
  cash: number | null
  weight: number | null
  label: string
  testId: string
  hint?: string
}) {
  return (
    <span className="flex flex-col md:items-end" data-label={label} title={hint} aria-label={hint ? `${label}: ${hint}` : undefined} tabIndex={hint ? 0 : undefined}>
      <span className="tab-nums whitespace-nowrap text-ink" data-testid={testId}>{cash === null ? '—' : formatKopecks(cash)}</span>
      <span className="tab-nums text-[10px] text-ink-faint">{formatPerGram(perGram(cash, weight))}</span>
    </span>
  )
}

function rowChargeHint(row: UnitEconomicsRow, include: (kind: string) => boolean): string | undefined {
  const lines = row.withholdings.length > 0
    ? row.withholdings.map((charge) => `${charge.label} ${formatKopecks(charge.amount)}`)
    : row.charges.filter((charge) => include(charge.kind)).map((charge) => `${charge.label} ${formatKopecks(charge.cash)}`)
  return lines.length > 0 ? lines.join(' · ') : undefined
}

function withholdingAmount(row: UnitEconomicsRow): number | null {
  return row.withholdings.length > 0
    ? row.withholdings.reduce((total, charge) => total + charge.amount, 0)
    : row.charges.some((charge) => !UNIT_SERVICE_CHARGE_KINDS.has(charge.kind) && charge.kind !== 'penalty' && charge.kind !== 'boost')
      ? unitOtherAmount(row)
      : null
}

function MobileFigure({ label, value, weight, tone, detail }: {
  label: string
  value: number | null
  weight: number | null
  tone?: string
  detail?: string
}) {
  return (
    <div className="min-w-0">
      <span className="block text-[10px] leading-tight text-ink-faint">{label}</span>
      <span className={`mt-1 block truncate text-[17px] font-semibold leading-none tab-nums ${tone ?? 'text-ink'}`}>{formatRub(value)}</span>
      <span className="mt-1 block truncate text-[10px] leading-tight tab-nums text-ink-faint">{detail ?? formatPerGram(perGram(value, weight))}</span>
    </div>
  )
}

function MobileDetail({ label, value, tone, visible = true }: {
  label: string
  value: string
  tone?: string
  visible?: boolean
}) {
  if (!visible) return null
  return (
    <div className="col-span-2 flex min-w-0 items-baseline justify-between gap-3 border-b border-rule/70 py-1.5 last:border-b-0">
      <dt className="min-w-0 text-[11px] leading-tight text-ink-faint">{label}</dt>
      <dd className={`shrink-0 text-right text-[12px] leading-tight tab-nums ${tone ?? 'text-ink'}`}>{value}</dd>
    </div>
  )
}

function MobilePieceCard({ row }: { row: UnitEconomicsRow }) {
  const weight = row.weightGrams
  const services = unitServiceAmount(row)
  const boost = unitChargeAmount(row, 'boost')
  const penalty = unitChargeAmount(row, 'penalty')
  const withholdings = withholdingAmount(row)
  const margin = row.margin === null
    ? formatRub(null)
    : `${formatRub(row.margin)}${row.marginOfCost === null ? '' : ` · ${formatPercent(row.marginOfCost)}`}`

  return (
    <div className="col-span-3 mt-3 min-w-0 md:hidden" data-testid={`unit-mobile-card-${row.orderId}`}>
      <div className="grid grid-cols-3 gap-2 border-y border-rule py-3">
        <MobileFigure label="Купили за" value={row.cost} weight={weight} detail={row.cost === null ? row.costReason ?? '—' : undefined} />
        <MobileFigure label="Получили" value={row.received} weight={weight} />
        <MobileFigure label="Прибыль" value={row.profit} weight={weight} tone={signClass(row.profit)} detail={row.profit === null ? row.profitReason ?? '—' : undefined} />
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3">
        <MobileDetail label="Продали за" value={formatKopecks(row.soldFor)} visible={row.soldFor !== 0} tone={row.soldFor < 0 ? 'text-[#b3261e]' : undefined} />
        <MobileDetail label="Услуги ЯМ" value={services === 0 ? '—' : formatKopecks(services)} visible={services !== 0} />
        <MobileDetail label="Буст" value={boost === null || boost === 0 ? '—' : formatKopecks(boost)} visible={boost !== null && boost !== 0} />
        <MobileDetail label="Штрафы" value={penalty === null || penalty === 0 ? '—' : formatKopecks(penalty)} visible={penalty !== null && penalty !== 0} />
        <MobileDetail label="Удержания" value={withholdings === null || withholdings === 0 ? '—' : formatKopecks(withholdings)} visible={withholdings !== null && withholdings !== 0} />
        <MobileDetail label="Маржа" value={margin} tone={signClass(row.margin)} />
        <MobileDetail label="СДЭК" value={row.cdekCost === null || row.cdekCost === 0 ? '—' : formatKopecks(row.cdekCost)} visible={row.cdekCost !== null && row.cdekCost !== 0} />
        <MobileDetail label="Налог" value={row.tax === null || row.tax === 0 ? '—' : formatKopecks(row.tax)} visible={row.tax !== null && row.tax !== 0} />
      </dl>
    </div>
  )
}

function PieceRow({ row, open, onToggle, onOpenCard }: {
  row: UnitEconomicsRow
  open: boolean
  onToggle: () => void
  onOpenCard: (productId: string) => void
}) {
  const weight = row.weightGrams
  const openCard = () => { if (row.productId) onOpenCard(row.productId) }
  return (
    <li className="border border-rule bg-paper p-3 md:border-0 md:border-b md:bg-transparent md:p-0 md:py-2.5" data-testid={`unit-row-${row.orderId}`}>
      <div
        role={row.productId ? 'button' : undefined}
        tabIndex={row.productId ? 0 : undefined}
        aria-label={row.productId ? `Открыть карточку ${row.article ?? row.shopSku}` : undefined}
        onClick={openCard}
        onKeyDown={(event) => { if (event.key === 'Enter') openCard() }}
        className={`relative grid grid-cols-[40px_minmax(0,1fr)_auto] gap-x-3 gap-y-1 ${GRID} ${row.productId ? 'cursor-pointer' : ''}`}
      >
        <span className="row-span-1 md:row-span-1">
          <WarehouseThumbnail mediaUrl={row.photoUrl} label={row.article ?? row.shopSku} unavailableReason={row.photoReason} size="large" />
        </span>
        <span className="min-w-0 text-[13px] text-ink">
          <span className="flex min-w-0 items-center gap-x-1.5 whitespace-nowrap">
            {row.article ? <CopyValue label="" value={row.article} truncate /> : <span className="min-w-0 truncate font-medium" title={row.shopSku}>{row.shopSku}</span>}
            {weight !== null ? <span className="shrink-0 text-ink-muted">{weight.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} г</span> : null}
          </span>
          <span className="block truncate text-[11px] text-ink-faint">{[row.category ? categoryLabel(row.category) : null, `заказ ${row.orderId}`].filter(Boolean).join(' · ')}</span>
        </span>
        <span className="col-start-3 row-start-1 self-start text-right text-[12px] text-ink-muted md:col-start-auto md:row-start-auto md:text-left md:text-[13px]">{dateLabel(row.orderCreatedAt)}</span>
        <span className="hidden md:contents">
          <CostValue row={row} />
          <Money value={row.soldFor} weight={weight} label="Продали за" tone={row.soldFor < 0 ? 'text-[#b3261e] font-medium' : 'font-medium text-ink'}
            testId={`unit-sold-for-${row.orderId}`} />
          <ChargeValue cash={unitServiceAmount(row)} weight={weight} label="Услуги ЯМ" testId={`unit-services-${row.orderId}`} />
          <ChargeValue cash={unitChargeAmount(row, 'boost')} weight={weight} label="Буст" testId={`unit-boost-${row.orderId}`} />
          <ChargeValue cash={unitChargeAmount(row, 'penalty')} weight={weight} label="Штрафы" testId={`unit-penalty-${row.orderId}`} />
          <ChargeValue cash={withholdingAmount(row)} weight={weight} label="Удержания" hint={rowChargeHint(row, (kind) => !UNIT_SERVICE_CHARGE_KINDS.has(kind) && kind !== 'penalty' && kind !== 'boost')} testId={`unit-other-charges-${row.orderId}`} />
          <Money value={row.received} weight={weight} label="Получили" testId={`unit-received-${row.orderId}`} />
          <Money value={row.margin} weight={weight} label="Маржа" tone={`font-semibold ${signClass(row.margin)}`} testId="unit-margin" />
          <span className={`tab-nums md:text-right ${signClass(row.marginOfCost)}`} data-label="%" title={row.marginOfSale !== null ? `${formatPercent(row.marginOfSale)} от цены продажи` : undefined}>
            {row.cost === null ? <span className="text-[11px] text-ink-faint">{row.costReason}</span> : formatPercent(row.marginOfCost)}
          </span>
          <Money value={row.cdekCost} weight={weight} label="СДЭК" />
          <Money value={row.tax} weight={weight} label="Налог" />
          <Money value={row.profit} weight={weight} label="Прибыль" tone={signClass(row.profit)} />
        </span>
        <MobilePieceCard row={row} />
        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? 'Скрыть расчёт' : 'Показать расчёт'}
          onClick={(event) => { event.stopPropagation(); onToggle() }}
          className="col-span-3 mt-3 flex w-full items-center justify-center gap-1 border-t border-rule pt-2 text-[12px] text-ink-muted hover:text-ink md:col-span-1 md:mt-0 md:size-7 md:w-7 md:border-0 md:p-0"
        >
          <span className="md:hidden">{open ? 'Скрыть расчёт' : 'Показать расчёт'}</span>
          <span className="hidden md:inline" aria-hidden="true">{open ? '▾' : '▸'}</span>
        </button>
      </div>
      {open ? <UnitBreakdown row={row} /> : null}
    </li>
  )
}

export function UnitEconomicsView() {
  const { resolution, today, changePeriod } = useAnalyticsPeriod()
  const period = resolution.status === 'ready' ? resolution.period : null
  const [filters, setFilters] = useState<UnitServerFilters>(DEFAULT_UNIT_FILTERS)
  const [load, setLoad] = useState<Load>({ kind: 'loading' })
  const [sort, setSort] = useState<{ key: UnitSortKey; ascending: boolean }>({ key: 'margin', ascending: true })
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const [span, setSpan] = useState<{ from: string; to: string } | null>(null)
  const [card, setCard] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const from = period?.range.from ?? null
  const to = period?.range.to ?? null

  useEffect(() => {
    if (from === null || to === null) return undefined
    const controller = new AbortController()
    setLoad({ kind: 'loading' })
    setSpan(null)
    fetchUnitEconomics({ ...filters, range: { from, to } }, controller.signal)
      .then((report) => { if (!controller.signal.aborted) setLoad({ kind: 'ready', report }) })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setLoad({ kind: 'error', message: error instanceof Error ? error.message : 'Не удалось загрузить юнит-экономику.' })
      })
    return () => controller.abort()
  }, [filters, from, to])

  const report = load.kind === 'ready' ? load.report : null
  const chart = useMemo(
    () => (report ? chartPoints(report.rows, { from: report.periodFrom, to: report.periodTo }) : null),
    [report],
  )
  const rows = useMemo(
    () => (report ? sortUnitRows(rowsInSpan(report.rows, span), sort.key, sort.ascending) : []),
    [report, span, sort],
  )
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize))
  const visibleRows = useMemo(() => rows.slice((page - 1) * pageSize, page * pageSize), [page, pageSize, rows])

  useEffect(() => {
    setPage((current) => Math.min(current, pageCount))
  }, [pageCount])

  const update = (patch: Partial<UnitServerFilters>) => {
    setPage(1)
    setFilters((current) => ({ ...current, ...patch }))
  }
  const toggle = (key: string) => setExpanded((current) => {
    const next = new Set(current)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })
  const chooseSort = (key: UnitSortKey) => {
    setPage(1)
    setSort((current) => ({ key, ascending: current.key === key ? !current.ascending : true }))
  }

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex flex-wrap items-center gap-2">
        {period ? <MarketMoneyPeriodPicker value={period} today={today} onChange={changePeriod} /> : <span className="text-[13px] text-ink-muted">Определяем период…</span>}
        {resolution.status === 'ready' && resolution.notice ? <span className="text-[12px] text-ink-muted">{resolution.notice}</span> : null}
      </div>
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Фильтры">
        <select
          aria-label="Категория"
          value={filters.category ?? ''}
          onChange={(event) => update({ category: event.target.value || null })}
          className="h-[31px] rounded-full border border-rule-strong bg-paper px-3 text-[13px] text-ink-2"
        >
          <option value="">Все категории</option>
          {(report?.categories ?? []).map((category) => <option key={category} value={category}>{categoryLabel(category)}</option>)}
        </select>
        <button type="button" aria-pressed={filters.boost === 'boosted'} onClick={() => update({ boost: filters.boost === 'boosted' ? null : 'boosted' })} className={chipClass(filters.boost === 'boosted')}>С бустом</button>
        <button type="button" aria-pressed={filters.boost === 'not_boosted'} onClick={() => update({ boost: filters.boost === 'not_boosted' ? null : 'not_boosted' })} className={chipClass(filters.boost === 'not_boosted')}>Без буста</button>
        <button type="button" aria-pressed={filters.tariff === 'old'} onClick={() => update({ tariff: filters.tariff === 'old' ? null : 'old' })} className={chipClass(filters.tariff === 'old')}>Старый тариф</button>
        <button type="button" aria-pressed={filters.tariff === 'new'} onClick={() => update({ tariff: filters.tariff === 'new' ? null : 'new' })} className={chipClass(filters.tariff === 'new')}>Новый тариф</button>
        <button type="button" aria-pressed={filters.costMissing} onClick={() => update({ costMissing: !filters.costMissing })} className={chipClass(filters.costMissing)}>Без себестоимости</button>
      </div>

      {load.kind === 'loading' ? <p className="text-[13px] text-ink-muted">Загрузка…</p> : null}
      {load.kind === 'error' ? <p role="alert" className="text-[13px] text-[#b3261e]">{load.message}</p> : null}
      {report && chart ? (
        <>
          <UnitSummaryCards
            totals={report.totals}
            costMissing={filters.costMissing}
            onCostMissing={() => update({ costMissing: !filters.costMissing })}
          />
          <section aria-label="Маржа за период" className="border border-rule bg-paper px-4 py-4">
            <h2 className="mb-2 text-[15px] font-semibold text-ink">Маржа {STEP_TITLES[chart.step]}</h2>
            <MarginChart
              testId="unit-period-chart"
              bars={periodBars(chart.points, chart.step)}
              selected={span?.from ?? null}
              onSelect={(key) => {
                const point = chart.points.find((candidate) => candidate.from === key)
                if (point) {
                  setPage(1)
                  setSpan((current) => (current?.from === point.from ? null : { from: point.from, to: point.to }))
                }
              }}
            />
          </section>

          {span ? (
            <p className="flex items-center gap-2 text-[12px] text-ink-2">
              Заказы за {spanLabel(span)}: {rows.length}
              <button type="button" onClick={() => { setPage(1); setSpan(null) }} className="rounded-full border border-rule-strong px-2 py-0.5 hover:border-ink">Показать весь период ×</button>
            </p>
          ) : null}
          <div className="md:overflow-x-auto">
          <div className="md:min-w-[1150px]">
          <div className={`hidden border-b border-rule-strong pb-1.5 text-[12px] text-ink-muted ${GRID}`}>
            <span aria-hidden="true" />
            <span>Изделие</span>
            {COLUMNS.map(([key, label]) => {
              const feeKey = key === 'marketWithheld' ? 'services' : key === 'otherCharges' ? 'withholdings' : key
              const hint = (feeKey === 'services' || feeKey === 'boost' || feeKey === 'withholdings')
                ? report.feeColumns.find((column) => column.key === feeKey)?.chargeKinds.join(' · ')
                : undefined
              return (
                <button key={key} type="button" title={hint} aria-label={hint ? `${label}: ${hint}` : label} onClick={() => chooseSort(key)} className={`text-left hover:text-ink ${key !== 'date' ? 'md:text-right' : ''} ${sort.key === key ? 'font-semibold text-ink' : ''}`}>
                  {label}{sort.key === key ? (sort.ascending ? ' ↑' : ' ↓') : ''}
                </button>
              )
            })}
            <span aria-hidden="true" />
          </div>
          <label className="flex items-center gap-2 text-[12px] text-ink-muted md:hidden">
            Сортировка
            <select aria-label="Сортировка" value={sort.key} onChange={(event) => {
              const key = COLUMNS.find(([value]) => value === event.target.value)?.[0]
              if (key) setSort({ key, ascending: true })
            }} className="h-[28px] rounded border border-rule-strong bg-paper px-2 text-[12px]">
              {COLUMNS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </label>
          {rows.length === 0
            ? <p className="text-[13px] text-ink-muted">Нет проданных изделий с расчётами Маркета за период.</p>
            : (
              <ul aria-label="Проданные изделия" className="space-y-2 md:space-y-0">
                {visibleRows.map((row) => {
                  const key = `${row.orderId}:${row.shopSku}`
                  return <PieceRow key={key} row={row} open={expanded.has(key)} onToggle={() => toggle(key)} onOpenCard={setCard} />
                })}
              </ul>
            )}
          </div>
          </div>
          {rows.length > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-2 text-[12px] text-ink-muted" aria-label="Пагинация">
              <span>{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, rows.length)} из {rows.length}</span>
              <label className="flex items-center gap-1.5">
                Строк на странице
                <select aria-label="Строк на странице" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1) }} className="h-[28px] rounded border border-rule-strong bg-paper px-2">
                  {[50, 100, 200].map((size) => <option key={size} value={size}>{size}</option>)}
                </select>
              </label>
              <div className="flex items-center gap-1">
                <button type="button" aria-label="Предыдущая страница" disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))} className="rounded border border-rule-strong px-2 py-1 disabled:opacity-40">←</button>
                <span>{page} / {pageCount}</span>
                <button type="button" aria-label="Следующая страница" disabled={page === pageCount} onClick={() => setPage((current) => Math.min(pageCount, current + 1))} className="rounded border border-rule-strong px-2 py-1 disabled:opacity-40">→</button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
      {card ? <UnitPiecePreview productId={card} onClose={() => setCard(null)} /> : null}
    </div>
  )
}
